import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from "react";

export interface CookieSettings {
    googleMaps: boolean;
}
interface CookieContextValue {
    cookies: CookieSettings;
    setCookie: (cookieName: keyof CookieSettings, value: boolean) => void;
}

export const CookieContext = createContext<CookieContextValue | null>(null);

const LocalStorage =
    typeof localStorage !== "undefined"
        ? localStorage
        : {
              getItem: () => {},
              setItem: () => {},
          };

function loadValue(
    key: string,
    initialValue: unknown,
    opts: { forceKeys: boolean },
) {
    const currentValue = LocalStorage.getItem(key);
    if (currentValue) {
        try {
            const json = JSON.parse(currentValue);
            if (opts.forceKeys) {
                return Object.keys(initialValue as object).every(
                    (key) => json[key] !== undefined,
                )
                    ? json
                    : initialValue;
            }
            return json;
        } catch (ignore) {
            return initialValue;
        }
    }
    return initialValue;
}

const useLocalStorage = (
    key: string,
    initialValue: unknown,
    opts: { forceKeys: boolean },
) => {
    // ! Always load initial value first, to not cause errors during hydration!
    const [value, setValue] = useState<CookieSettings>(
        initialValue as CookieSettings,
    );

    useEffect(() => {
        setValue(loadValue(key, initialValue, opts));
    }, []);

    useEffect(() => {
        LocalStorage.setItem(key, JSON.stringify(value));
    }, [value]);

    const setCookie = useCallback<CookieContextValue["setCookie"]>(
        (cookieName, value) => {
            setValue((currVal) => {
                currVal[cookieName] = value;
                return { ...currVal };
            });
        },
        [setValue],
    );

    return [value, setCookie] as [typeof value, typeof setCookie];
};

export const CookieProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [cookies, setCookie] = useLocalStorage(
        "cookies",
        {
            googleMaps: false,
        },
        { forceKeys: true },
    );

    return (
        <CookieContext.Provider value={{ cookies, setCookie }}>
            <>{children}</>
        </CookieContext.Provider>
    );
};

export const useCookies = () => {
    const context = useContext(CookieContext);
    if (!context) {
        throw new Error("context provider missing");
    }
    return context;
};

export const useCookie = <T extends keyof CookieSettings>(cookieName: T) => {
    const { cookies, setCookie } = useCookies();
    return [cookies[cookieName], (value) => setCookie(cookieName, value)] as [
        CookieSettings[T],
        (value: Parameters<CookieContextValue["setCookie"]>[1]) => void,
    ];
};
